import { AHText, Box, Card, Circle, useScaleFactor, useTheme } from "arkh";
import { DateTime, FixedOffsetZone, IANAZone } from "luxon";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Path, Svg } from "react-native-svg";
import { graphql, useFragment, useSubscription } from "react-relay";
import { InfoBoxSubscription } from "./__generated__/InfoBoxSubscription.graphql";
import { InfoBox_StorecastScreen$key } from "./__generated__/InfoBox_StorecastScreen.graphql";

interface Props {
  screen: InfoBox_StorecastScreen$key;
}

export const InfoBox = ({ screen }: Props) => {
  const intl = useIntl();

  const data = useFragment(
    graphql`
      fragment InfoBox_StorecastScreen on StorecastScreen {
        id
        info {
          language
          languageStatus
          resolution
          resolutionStatus
          time
          timeStatus
          timeZone
          timeZoneStatus
          userAgentStatus
        }
      }
    `,
    screen
  );
  const info = data.info;

  useSubscription<InfoBoxSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription InfoBoxSubscription($screenId: ID!) {
            storecastScreenInfoChange(screenId: $screenId) {
              ...InfoBox_StorecastScreen
            }
          }
        `,
        variables: { screenId: data.id },
      }),
      [data.id]
    )
  );

  const time = useMemo(
    () => (info.time ? DateTime.fromMillis(info.time).toFormat("HH:mm") : null),
    [info.time]
  );

  const timeZone = useMemo(() => {
    if (!info.timeZone) return null;
    if (IANAZone.isValidZone(info.timeZone)) {
      return IANAZone.create(info.timeZone);
    }
    return FixedOffsetZone.parseSpecifier(info.timeZoneStatus);
  }, [info.timeZone, info.timeZoneStatus]);

  const prettyTimeZone = useMemo(() => {
    if (timeZone instanceof IANAZone) {
      return timeZone.name.split("/").at(-1);
    } else if (timeZone instanceof FixedOffsetZone) {
      return `UTC ${timeZone.formatOffset(
        info.time ?? DateTime.now().toMillis(),
        "narrow"
      )}`;
    }
  }, [info.time, timeZone]);

  let formattedLanguage: string | null = info.language;
  switch (info.language) {
    case "sv":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language sv",
        defaultMessage: "Swedish",
      });
      break;

    case "en":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language en",
        defaultMessage: "English",
      });
      break;

    case "nb":
      formattedLanguage = intl.formatMessage({
        description: "Screen status language nb",
        defaultMessage: "Norwegian",
      });
      break;
  }

  return (
    <Card color="surface-primary" defaultBorder>
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status time",
          defaultMessage: "Time",
        })}
        value={time ?? "?"}
        status={info.timeStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status time zone",
          defaultMessage: "Time zone",
        })}
        value={prettyTimeZone ?? "?"}
        status={info.timeZoneStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status language",
          defaultMessage: "Language",
        })}
        value={formattedLanguage ?? "?"}
        status={info.languageStatus}
      />
      <StatusItem
        label={intl.formatMessage({
          description: "Screen status resolution",
          defaultMessage: "Resolution",
        })}
        value={info.resolution ?? "?"}
        status={info.resolutionStatus}
      />
      <StatusItem
        label=""
        value={intl.formatMessage({
          description: "Screen status browser version",
          defaultMessage: "Browser version",
        })}
        status={info.userAgentStatus}
        last
      />
    </Card>
  );
};

const StatusItem = ({
  label,
  value,
  status,
  last,
}: {
  label: string;
  value: string;
  status: "positive" | "semiNegative" | "negative";
  last?: boolean;
}) => {
  return (
    <Box
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      spaceBottom={last ? "none" : "medium"}
    >
      <AHText type="list-item-title">
        {label === "" ? "" : label + " "}
        <AHText type="title-small">{value}</AHText>
      </AHText>

      <StatusIcon type={status} />
    </Box>
  );
};

const StatusIcon = ({
  type,
}: {
  type: "positive" | "semiNegative" | "negative";
}) => {
  const theme = useTheme();
  const sf = useScaleFactor();

  return (
    <Circle size="smallMedium" borderWidth={sf * 2} borderColor={type}>
      <Svg viewBox="4 4 16 16" fill="none" width={sf * 16} height={sf * 16}>
        {type === "positive" ? (
          <Path
            d="M 16,8.9999699 10.5,14.5 8,12"
            stroke={theme["positive"]}
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <Path
              d="M 12,7.9709749 V 11.970925"
              stroke={theme[type]}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              d="m 12,15.971225 h 0.01"
              stroke={theme[type]}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </Svg>
    </Circle>
  );
};
